// apps/mobile/src/app/services/dataService.ts
import apiClient, { API_URL } from './apiClient';
import type { ApiRequestOptions } from './apiClient';
import type {
  ConferenceEvent,
  Session,
  Paper,
  PaperDetail,
  PaperFigure,
  Profile,
  Exhibitor,
  PartnerDirectory,
  SponsorDirectory,
  IndustryPartnerDirectory,
  Committee,
  AppNotification,
  Note,
  Favorite,
} from '@conference/shared';

// ─── Events ──────────────────────────────────────────────────────────────────

export const eventService = {
  async getAll(): Promise<ConferenceEvent[]> {
    return apiClient.get<ConferenceEvent[]>('/events');
  },
  async getById(id: string, options: ApiRequestOptions = {}): Promise<ConferenceEvent | null> {
    return apiClient.get<ConferenceEvent>(`/events/${id}`, options);
  },
  async getUpcoming(options: ApiRequestOptions = {}): Promise<ConferenceEvent[]> {
    return apiClient.get<ConferenceEvent[]>('/events?status=published,live', options);
  },
  async getPast(options: ApiRequestOptions = {}): Promise<ConferenceEvent[]> {
    return apiClient.get<ConferenceEvent[]>('/events?status=completed', options);
  },
};

// ─── Sessions ────────────────────────────────────────────────────────────────

export const sessionService = {
  async getAll(eventId: string): Promise<Session[]> {
    return apiClient.get<Session[]>(`/sessions?event_id=${eventId}`);
  },
  async getById(id: string, eventId: string, options: ApiRequestOptions = {}): Promise<Session | null> {
    return apiClient.get<Session>(`/sessions/${id}?event_id=${encodeURIComponent(eventId)}`, options);
  },
  async getByDate(eventId: string, date: string): Promise<Session[]> {
    return apiClient.get<Session[]>(`/sessions?event_id=${eventId}&date=${date}`);
  },
  async getLive(eventId: string): Promise<Session[]> {
    return apiClient.get<Session[]>(`/sessions?event_id=${eventId}&status=live`);
  },
  async search(eventId: string, query: string): Promise<Session[]> {
    return apiClient.get<Session[]>(`/sessions?event_id=${eventId}&q=${encodeURIComponent(query)}`);
  },
};

// ─── Papers ──────────────────────────────────────────────────────────────────

export const paperService = {
  async getAll(eventId: string): Promise<Paper[]> {
    return apiClient.get<Paper[]>(`/papers?event_id=${eventId}`);
  },
  async getById(id: string, eventId: string, options: ApiRequestOptions = {}): Promise<PaperDetail | null> {
    return apiClient.get<PaperDetail>(`/papers/${id}?event_id=${encodeURIComponent(eventId)}`, options);
  },
  async getFigureBlob(paperId: string, eventId: string, figure: PaperFigure, options: ApiRequestOptions = {}): Promise<Blob> {
    const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
    if (![paperId, eventId, figure.generation_id].every((id) => uuid.test(id))
      || !/^[0-9a-f]{64}$/.test(figure.asset_id)) {
      throw new Error('Invalid figure identity');
    }
    return apiClient.getBlob(`/papers/${paperId}/figures/${figure.generation_id}/${figure.asset_id}?event_id=${eventId}&preview=true`,
      { ...options, maxBytes: 1024 * 1024, allowedTypes: ['image/jpeg'] });
  },
  async getFeatured(eventId: string): Promise<Paper[]> {
    return apiClient.get<Paper[]>(`/papers?event_id=${eventId}&featured=true`);
  },
  async search(eventId: string, query: string): Promise<Paper[]> {
    return apiClient.get<Paper[]>(`/papers?event_id=${eventId}&q=${encodeURIComponent(query)}`);
  },
  async getPdfUrl(pdfPath: string): Promise<string> {
    return `${API_URL}/files/${pdfPath}`;
  },
};

// ─── Speakers ────────────────────────────────────────────────────────────────

export const speakerService = {
  async getAll(eventId: string): Promise<Profile[]> {
    return apiClient.get<Profile[]>(`/speakers?event_id=${eventId}`);
  },
  async getById(
    id: string,
    eventId: string,
    options: ApiRequestOptions = {},
  ): Promise<Profile | null> {
    if (!eventId) {
      throw new Error('Select an event before viewing speaker profiles.');
    }
    return apiClient.get<Profile>(
      `/speakers/${id}?event_id=${encodeURIComponent(eventId)}`,
      options,
    );
  },
  async search(eventId: string, query: string): Promise<Profile[]> {
    return apiClient.get<Profile[]>(`/speakers?event_id=${eventId}&q=${encodeURIComponent(query)}`);
  },
};

// ─── Exhibitors ──────────────────────────────────────────────────────────────

export const exhibitorService = {
  async getAll(eventId: string): Promise<Exhibitor[]> {
    return apiClient.get<Exhibitor[]>(`/exhibitors?event_id=${eventId}`);
  },
  async getById(id: string, eventId: string): Promise<Exhibitor | null> {
    return apiClient.get<Exhibitor>(`/exhibitors/${id}?event_id=${encodeURIComponent(eventId)}`);
  },
  async search(eventId: string, query: string): Promise<Exhibitor[]> {
    return apiClient.get<Exhibitor[]>(`/exhibitors?event_id=${eventId}&q=${encodeURIComponent(query)}`);
  },
};

export const partnerService = {
  async getDirectory(eventId: string): Promise<PartnerDirectory> {
    return apiClient.get<PartnerDirectory>(`/events/${encodeURIComponent(eventId)}/partners`);
  },
  async getSponsors(eventId: string): Promise<SponsorDirectory> {
    return apiClient.get<SponsorDirectory>(`/events/${encodeURIComponent(eventId)}/sponsors`);
  },
  async getIndustryPartners(eventId: string): Promise<IndustryPartnerDirectory> {
    return apiClient.get<IndustryPartnerDirectory>(`/events/${encodeURIComponent(eventId)}/industry-partners`);
  },
};

export const eventContentService = {
  async getCommittee(eventId: string): Promise<Committee> {
    return apiClient.get<Committee>(`/events/${encodeURIComponent(eventId)}/committee`);
  },
};

// ─── Attendees ───────────────────────────────────────────────────────────────

export const attendeeService = {
  async getAll(eventId: string): Promise<Profile[]> {
    return apiClient.get<Profile[]>(`/attendees?event_id=${eventId}`);
  },
  async getById(
    id: string,
    eventId: string,
    options: ApiRequestOptions = {},
  ): Promise<Profile | null> {
    if (!eventId) {
      throw new Error('Select an event before viewing attendee profiles.');
    }
    return apiClient.get<Profile>(`/attendees/${id}?event_id=${eventId}`, options);
  },
  async search(eventId: string, query: string): Promise<Profile[]> {
    return apiClient.get<Profile[]>(`/attendees?event_id=${eventId}&search=${encodeURIComponent(query)}`);
  },
};

// ─── Notes ───────────────────────────────────────────────────────────────────

export const noteService = {
  async getAll(_userId: string, options: ApiRequestOptions = {}): Promise<Note[]> {
    // userId not needed — API scopes by JWT automatically
    return apiClient.get<Note[]>('/notes', options);
  },
  async getById(id: string): Promise<Note | null> {
    return apiClient.get<Note>(`/notes/${id}`);
  },
  async create(note: Omit<Note, 'id' | 'created_at' | 'updated_at'>, options: ApiRequestOptions = {}): Promise<Note> {
    return apiClient.post<Note>('/notes', note, options);
  },
  async update(id: string, updates: Partial<Pick<Note, 'title' | 'content'>>, options: ApiRequestOptions = {}): Promise<Note> {
    return apiClient.patch<Note>(`/notes/${id}`, updates, options);
  },
  async delete(id: string, options: ApiRequestOptions = {}): Promise<void> {
    return apiClient.delete(`/notes/${id}`, options);
  },
};

// ─── Favorites ───────────────────────────────────────────────────────────────

export const favoriteService = {
  async getAll(
    _userId: string,
    eventId: string | null = null,
    options: ApiRequestOptions = {},
  ): Promise<Favorite[]> {
    const path = eventId
      ? `/favorites?event_id=${encodeURIComponent(eventId)}`
      : '/favorites';
    return apiClient.get<Favorite[]>(path, options);
  },
  async add(
    _userId: string,
    entityType: string,
    entityId: string,
    eventId: string | null = null,
    options: ApiRequestOptions = {},
  ): Promise<Favorite> {
    return apiClient.post<Favorite>('/favorites', {
      entity_type: entityType,
      entity_id: entityId,
      ...(entityType === 'attendee' && eventId ? { event_id: eventId } : {}),
    }, options);
  },
  async remove(_userId: string, entityType: string, entityId: string, options: ApiRequestOptions = {}): Promise<void> {
    return apiClient.delete(`/favorites?entity_type=${entityType}&entity_id=${entityId}`, options);
  },
  async isFavorited(
    _userId: string,
    entityType: string,
    entityId: string,
    eventId: string | null = null,
  ): Promise<boolean> {
    const path = eventId
      ? `/favorites?event_id=${encodeURIComponent(eventId)}`
      : '/favorites';
    const favs = await apiClient.get<Favorite[]>(path);
    return favs.some((f) => f.entity_type === entityType && f.entity_id === entityId);
  },
};

// ─── Direct Messages ─────────────────────────────────────────────────────────

export interface MessageOut {
  id: string;
  sender_id: string;
  receiver_id: string;
  content: string;
  is_read: boolean;
  created_at: string;
}

export interface ConversationSummary {
  partner_id: string;
  partner_name: string;
  partner_avatar_url?: string | null;
  partner_title?: string | null;
  partner_company?: string | null;
  last_message: string;
  last_message_at: string;
  unread_count: number;
  is_last_mine: boolean;
}

export const messageService = {
  async getConversations(options: ApiRequestOptions = {}): Promise<ConversationSummary[]> {
    return apiClient.get<ConversationSummary[]>('/messages', options);
  },
  async getMessages(otherUserId: string, options: ApiRequestOptions = {}): Promise<MessageOut[]> {
    return apiClient.get<MessageOut[]>(`/messages/${otherUserId}`, options);
  },
  async send(receiverId: string, content: string, eventId?: string, options: ApiRequestOptions = {}): Promise<MessageOut> {
    return apiClient.post<MessageOut>('/messages', { receiver_id: receiverId, content, event_id: eventId }, options);
  },
  async markRead(otherUserId: string, options: ApiRequestOptions = {}): Promise<void> {
    return apiClient.patch(`/messages/${otherUserId}/read`, {}, options);
  },
};

// ─── Notifications ───────────────────────────────────────────────────────────

export const notificationService = {
  async getAll(_userId: string, options: ApiRequestOptions = {}): Promise<AppNotification[]> {
    return apiClient.get<AppNotification[]>('/notifications', options);
  },
  async markRead(id: string, options: ApiRequestOptions = {}): Promise<void> {
    return apiClient.patch(`/notifications/${id}/read`, {}, options);
  },
  async markAllRead(_userId: string, options: ApiRequestOptions = {}): Promise<void> {
    return apiClient.patch('/notifications/read-all', {}, options);
  },
  getUnreadCount(notifications: AppNotification[]): number {
    return notifications.filter((n) => !n.read).length;
  },
};

// ─── Membership ──────────────────────────────────────────────────────────────

export interface MyEvent {
  id: string;
  name: string;
  date_start: string;
  date_end: string;
  location: string;
  status: string;
  role: string;
  ticket_type: string;
}

export interface DiscoverEvent {
  id: string;
  name: string;
  description?: string;
  date_start: string;
  date_end: string;
  location: string;
  venue?: string;
  image_url?: string;
  website?: string;
}

export interface JoinResult {
  event_id: string;
  role: string;
  already_joined: boolean;
}

export const membershipService = {
  async joinWithCode(code: string, options: ApiRequestOptions = {}): Promise<JoinResult> {
    return apiClient.post<JoinResult>('/events/join', { code }, options);
  },
  async listMyEvents(options: ApiRequestOptions = {}): Promise<MyEvent[]> {
    return apiClient.get<MyEvent[]>('/me/events', options);
  },
  async listDiscover(): Promise<DiscoverEvent[]> {
    return apiClient.get<DiscoverEvent[]>('/events/discover');
  },
};

// ─── Unified export (backwards-compatible) ───────────────────────────────────

export const dataService = {
  events: eventService,
  sessions: sessionService,
  papers: paperService,
  speakers: speakerService,
  exhibitors: exhibitorService,
  attendees: attendeeService,
  notes: noteService,
  favorites: favoriteService,
  notifications: notificationService,
  membership: membershipService,
};

export default dataService;
